import { APP_EMITTER_EVENTS, AppEmitter, FormLabel, IValidateError, NumericInput } from "shared";
import { ITemplateMoneyComponent } from "entities/template";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IValidationFunc } from "../../../../lib/types/validation";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { useTranslation } from "react-i18next";
import { ComponentTooltip, IComponentTooltipSettings } from "../component_tooltip/component_tooltip";
import { spellNumber } from "../../../../lib/utils/spell_number";
import { TAG_PREFIX } from "../../../../lib/const/const";
import styles from "./document_form_money.module.scss";


interface IDocumentFormMoneyProps {
    formRecord: Record<string, any>;
    component: ITemplateMoneyComponent;
    onChange: (partial: Record<string, any>) => void;
    onValidationRegister: (validationFunc: IValidationFunc, tag: string) => void;
    externalUpdateRecord: Record<string, any>;
    label?: string;
    tooltip: IComponentTooltipSettings;
    readonly: boolean;
    enLanguageEnabled: boolean;
    triggerValidate: number;
    labelShort: boolean;
}

export const DocumentFormMoney: React.FC<IDocumentFormMoneyProps> = React.memo(({
                                                                                    component,
                                                                                    formRecord,
                                                                                    label,
                                                                                    labelShort,
                                                                                    enLanguageEnabled,
                                                                                    triggerValidate,
                                                                                    readonly,
                                                                                    tooltip,
                                                                                    onValidationRegister,
                                                                                    onChange,
                                                                                    externalUpdateRecord,
                                                                                }) => {
    const t = useComponentsTranslations();
    const { i18n } = useTranslation();
    const [spellValue, setSpellValue] = useState<string>();
    const [value, setValue] = useState<string>("");

    useEffect(() => {
        return () => {
            onValidationRegister(() => {
                return Promise.resolve(true);
            }, component.name);
            onChange({ [component.name]: undefined, [TAG_PREFIX + component.name]: undefined });
        };
    }, []);

    useEffect(() => {
        AppEmitter.on(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        return () => {
            AppEmitter.off(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        };
    }, []);

    useEffect(() => {
        onValidationRegister(validateMoney, component.name);
    }, [component, formRecord]);

    useEffect(() => {
        const updateVal = externalUpdateRecord[TAG_PREFIX + component.name];
        if (component.name) {
            onMoneyChange(updateVal ? updateVal : "");
        }
    }, [externalUpdateRecord, component.name]);

    const validate = useMemo(() => {
        return component.required !== false ||
            !!component.maxNumber ||
            !!component.minNumber;
    }, [component]);

    const required = useMemo(() => {
        return component.required !== false;
    }, [component]);

    const handleClearEvent = () => {
        if (!readonly) {
            onMoneyChange("");
        }
    };

    const validateFunction = useCallback((value?: string | null): IValidateError => {

        if (required && !value) {
            return {
                valid: false,
                message: t("requiredFieldError")
            };
        }

        if (value) {
            try {
                if (component.maxNumber && parseInt(value) > parseInt(component.maxNumber)) {
                    return {
                        valid: false,
                        message: t("greaterThanMaxNumber", { maxNumber: component.maxNumber })
                    };
                }
                if (component.minNumber && parseInt(value) < parseInt(component.minNumber)) {
                    return {
                        valid: false,
                        message: t("lessThanMinNumber", { minNumber: component.minNumber })
                    };
                }
            } catch (e) {
                return {
                    valid: false,
                    message: t("invalidValue")
                };
            }
        }

        return {
            valid: true
        };
    }, [component, i18n.language]);

    const validateMoney = (): Promise<boolean> => {
        const valid = !required || validateFunction(formRecord[TAG_PREFIX + component.name]).valid;
        return Promise.resolve(valid);
    };

    const onMoneyChange = (val?: string) => {
        const componentLanguage = enLanguageEnabled && component.useEnValue ? "en" : "ru";
        const spell = spellNumber(val, "RUB", componentLanguage);
        setSpellValue(spell);
        setValue(val ? val : "");
        onChange({ [component.name]: spell, [TAG_PREFIX + component.name]: val });
    };

    return component.name ? <FormLabel title={label} required={required} disabled={readonly} labelShort={labelShort}>
        <div className={styles.container}>
            <NumericInput
                value={value}
                decimalScale={2}
                className={styles.input}
                hint={spellValue}
                validate={validate}
                validateFunc={validateFunction}
                triggerValidate={triggerValidate}
                onUpdate={(value: string | null) => onMoneyChange(value ? value : undefined)}
                disabled={readonly}
                displayType="input"
            />
            <ComponentTooltip tooltip={tooltip} id={component.id.toString()} className={styles.tooltip}/>
        </div>
    </FormLabel> : null;
});
